const START_STATE = 'start';

const allowedTypesByPreviousType = {
  [START_STATE]: ['entry'],
  entry: ['pause', 'exit'],
  resume: ['pause', 'exit'],
  pause: ['resume'],
  exit: ['entry'],
};

const defaultTypeByPreviousType = {
  [START_STATE]: 'entry',
  entry: 'exit',
  resume: 'exit',
  pause: 'resume',
  exit: 'entry',
};

export const getAllowedTypesAfter = (previousType) => (
  allowedTypesByPreviousType[previousType || START_STATE] || []
);

export const getDefaultTypeAfter = (previousType) => (
  defaultTypeByPreviousType[previousType || START_STATE] || null
);

export const isTypeValidAfter = (previousType, type) => (
  getAllowedTypesAfter(previousType).includes(type)
);

export const normalizeFollowingSignTypes = (signs, changedIndex) => {
  if (!Array.isArray(signs) || changedIndex < 0) return [];

  const changes = [];

  for (let i = changedIndex + 1; i < signs.length; i++) {
    const sign = signs[i];
    if (!sign) continue;

    const previousType = signs[i - 1]?.type;
    const allowedTypes = getAllowedTypesAfter(previousType);

    if (sign.type && allowedTypes.includes(sign.type)) {
      break;
    }

    const normalizedType = getDefaultTypeAfter(previousType);
    if (!normalizedType) break;

    if (sign.type !== normalizedType) {
      sign.type = normalizedType;
      changes.push({ index: i, type: normalizedType });
    }
  }

  return changes;
};

export const normalizeSignTypesByOrder = (signs) => {
  if (!Array.isArray(signs)) return [];

  const signedItems = signs
    .map((sign, index) => ({ sign, index }))
    .filter(({ sign }) => sign && sign.sign !== null);

  const remainingTypes = new Map();
  signedItems.forEach(({ sign }) => {
    remainingTypes.set(sign.type, (remainingTypes.get(sign.type) || 0) + 1);
  });

  const failedStates = new Set();
  const findValidOrder = (position, previousType) => {
    if (position === signedItems.length) return [];

    const stateKey = `${position}:${previousType || START_STATE}:${Array.from(remainingTypes.entries())
      .sort(([typeA], [typeB]) => String(typeA).localeCompare(String(typeB)))
      .map(([type, count]) => `${type}-${count}`)
      .join(',')}`;
    if (failedStates.has(stateKey)) return null;

    const currentType = signedItems[position].sign.type;
    const candidates = getAllowedTypesAfter(previousType)
      .filter(type => remainingTypes.get(type) > 0)
      .sort((typeA, typeB) => {
        if (typeA === currentType) return -1;
        if (typeB === currentType) return 1;
        return 0;
      });

    for (const type of candidates) {
      remainingTypes.set(type, remainingTypes.get(type) - 1);
      const followingTypes = findValidOrder(position + 1, type);
      remainingTypes.set(type, remainingTypes.get(type) + 1);

      if (followingTypes) return [type, ...followingTypes];
    }

    failedStates.add(stateKey);
    return null;
  };

  const orderedTypes = findValidOrder(0, null);
  if (!orderedTypes) return [];

  const changes = [];
  signedItems.forEach(({ sign, index }, orderedIndex) => {
    const type = orderedTypes[orderedIndex];
    if (sign.type !== type) {
      sign.type = type;
      changes.push({ index, type });
    }
  });

  return changes;
};
